import type {TranslationFunction} from 'src/types/language-support';
import type {
  CreateTaskPayload,
  DateFilterPreset,
  InitialTaskReminderValues,
  ReminderSelection,
  SnoozeOption,
  TabConfig,
  TaskAssignmentType,
  TaskCandidateDetails,
  TaskDetailsResponse,
  TaskDueStatus,
  TaskEntityMapping,
  TaskJobDetails,
  TaskListParams,
  TaskMetadata,
  TaskMetadataDisplayCtx,
  TaskReminderApiReminder,
  TaskReminderFormState,
  TaskReminderFormValues,
  TaskTab,
  UpdateTaskPayload,
} from 'src/types/task-and-reminder';
import type {RerouterLocation} from 'src/rerouter/types';

import moment from 'moment-timezone';
import {AnalyticsService} from 'src/analytics';
import {TASK_AND_REMINDER} from 'src/analytics/constants_event';


export const getUTCDateWithTimezone = (date: string): string => {
  if (!date.trim()) {
    return '';
  }
  const localTz = moment.tz.guess();
  return moment(date).tz(localTz).format('DD MMM, YYYY [at] hh:mm A');
};

const convertDateTimeToIsoString = (dateTime: moment.Moment): string =>
  dateTime.toISOString();

export function formatToCustomDateString(
  inputStr: string,
  dateFormat: string = 'ddd, M/D/YY, h:mm A',
  locale?: string,
): string {
  const dt = moment.utc(inputStr).locale(locale || 'en');

  return dt.tz(moment.tz.guess()).format(dateFormat);
}

export const MAX_REMINDERS = 3;

const REMINDER_OPTION_TO_DURATION: Record<string, TaskReminderApiReminder> = {
  ONE_HOUR_BEFORE: {amount: 1, unit: 'hours'},
  THREE_HOURS_BEFORE: {amount: 3, unit: 'hours'},
  ONE_DAY_BEFORE: {amount: 1, unit: 'days'},
  TWO_DAYS_BEFORE: {amount: 2, unit: 'days'},
  THREE_DAYS_BEFORE: {amount: 3, unit: 'days'},
  ONE_WEEK_BEFORE: {amount: 1, unit: 'weeks'},
  TWO_WEEKS_BEFORE: {amount: 2, unit: 'weeks'},
};
const DURATION_TO_REMINDER_OPTION: Record<string, string> = Object.entries(
  REMINDER_OPTION_TO_DURATION,
).reduce<Record<string, string>>(
  (accumulator, [optionKey, reminderDuration]) => {
    const durationKey = `${reminderDuration.amount}_${reminderDuration.unit}`;
    accumulator[durationKey] = optionKey;
    return accumulator;
  },
  {},
);

export const getReminderOptions = (
  t: TranslationFunction,
): Array<{key: string; label: string}> => [
  {key: 'ONE_HOUR_BEFORE', label: t('1_hour_before', '1 hour before')},
  {key: 'THREE_HOURS_BEFORE', label: t('3_hours_before', '3 hours before')},
  {key: 'ONE_DAY_BEFORE', label: t('1_day_before', '1 day before')},
  {key: 'TWO_DAYS_BEFORE', label: t('2_days_before', '2 days before')},
  {key: 'THREE_DAYS_BEFORE', label: t('3_days_before', '3 days before')},
  {key: 'ONE_WEEK_BEFORE', label: t('1_week_before', '1 week before')},
  {key: 'TWO_WEEKS_BEFORE', label: t('2_weeks_before', '2 weeks before')},
];

export const getTaskAssigneeFilterOptions = (
  t: TranslationFunction,
): Array<{key: string; label: string}> => [
  {key: 'all', label: t('All_Assignee', 'All Assignee')},
  {key: 'me', label: t('Assigned_to_Me', 'Assigned to Me')},
  {key: 'team', label: t('My_Team', 'My Team')},
  {key: 'cc', label: t('CC\'d_On', 'CC\'d On')},
];

export const getTaskDateFilterOptions = (
  t: TranslationFunction,
  activeTab: TaskTab,
): Array<{key: string; label: string}> =>
  activeTab === 'upcoming'
    ? [
        {key: 'next_7', label: t('Next_7_Days', 'Next 7 Days')},
        {key: 'next_30', label: t('Next_30_Days', 'Next 30 Days')},
        {key: 'custom', label: t('Custom_Date', 'Custom Date')},
      ]
    : [
        {key: 'last_7', label: t('Last_7_Days', 'Last 7 Days')},
        {key: 'last_30', label: t('Last_30_Days', 'Last 30 Days')},
        {key: 'custom', label: t('Custom_Date', 'Custom Date')},
      ];

export const getDefaultFormValues = (): TaskReminderFormState => ({
  title: '',
  description: '',
  task_type: 'generic',
  reminders: [],
  assignee_id: '',
  cc: [],
  due_date: moment().format('YYYY-MM-DD'),
  due_time: moment().add(30, 'minutes').startOf('hour').format('HH:mm'),
  job_details: null,
  candidate_details: null,
});

export const createReminderSelection = (option = ''): ReminderSelection => ({
  id: `reminder_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  option,
});

const JOB_INFO_PATH_PATTERN = /\/crm\/job\/(\d+)\/info\b/;

export const buildTaskPageEntityDefaults = (
  location: RerouterLocation,
  jobInfo: any,
  candidateInfo: any,
): {job_details?: TaskJobDetails; candidate_details?: TaskCandidateDetails} => {
  const {pathname, query} = location;
  const {applicantId, candidateId} = query;
  const jobId = pathname.match(JOB_INFO_PATH_PATTERN)?.[1] ?? null;

  let entityData = {};
  if (jobId) {
    entityData = {
      ...entityData,
      job_details: {id: jobId, title: jobInfo?.title},
    };
  }

  if ((applicantId || candidateId) && candidateInfo) {
    entityData = {
      ...entityData,
      candidate_details: {id: candidateInfo.id, name: candidateInfo.name},
    };
  }
  return entityData;
};

export const normalizeInitialValues = (
  initialValues?: InitialTaskReminderValues,
): TaskReminderFormState => {
  const defaultValues = getDefaultFormValues();
  if (!initialValues) {
    return defaultValues;
  }

  const normalizedReminders =
    initialValues.reminders ??
    (initialValues.reminderOption ? [initialValues.reminderOption] : []);
  const normalizedCc = Array.isArray(initialValues.cc) ? initialValues.cc : [];

  return {
    ...defaultValues,
    ...initialValues,
    reminders: normalizedReminders.map((option) =>
      createReminderSelection(option),
    ),
    cc: normalizedCc,
    job_details: initialValues.job_details ?? defaultValues.job_details,
    candidate_details:
      initialValues.candidate_details ?? defaultValues.candidate_details,
  };
};

const parseDescription = (description: string): Record<string, unknown> => {
  if (!description) {
    return {};
  }

  try {
    const parsedDescription = JSON.parse(description);
    return parsedDescription && typeof parsedDescription === 'object'
      ? parsedDescription
      : {};
  } catch (_error) {
    return {};
  }
};

const mapReminderOptionsToApiReminders = (
  reminderOptions: Array<string>,
): Array<TaskReminderApiReminder> =>
  reminderOptions
    .map((option) => REMINDER_OPTION_TO_DURATION[option])
    .filter(Boolean);

const mapApiRemindersToReminderOptions = (
  reminders: Array<TaskReminderApiReminder> = [],
): Array<string> =>
  reminders
    .map((reminder) => {
      const durationKey = `${reminder?.amount}_${reminder?.unit}`;
      return DURATION_TO_REMINDER_OPTION[durationKey];
    })
    .filter(Boolean);

/**
 * Interprets due date + time as wall-clock in `timezone` (defaults to browser guess),
 * then returns that instant as UTC ISO 8601 for the API.
 */
const getDueDateTime = (
  dueDate: string,
  dueTime: string,
  timezone?: string,
): string => {
  const tz = timezone ?? moment.tz.guess();
  const datePart = dueDate.trim() || moment().tz(tz).format('YYYY-MM-DD');
  const timePart =
    dueTime.trim() ||
    moment().tz(tz).startOf('day').add(9, 'hours').format('HH:mm');
  const parsed = moment.tz(`${datePart} ${timePart}`, 'YYYY-MM-DD HH:mm', tz);
  if (!parsed.isValid()) {
    return moment.utc().toISOString();
  }
  return parsed.toISOString();
};

const getDescriptionFromTask = (description: unknown): string => {
  if (
    !description ||
    typeof description !== 'object' ||
    Array.isArray(description)
  ) {
    return '';
  }

  return Object.keys(description).length ? JSON.stringify(description) : '';
};

const coerceJobDetailsFromMetadata = (raw: unknown): TaskJobDetails | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id =
    record.id !== undefined && record.id !== null
      ? String(record.id).trim()
      : '';
  const title = typeof record.title === 'string' ? record.title.trim() : '';
  if (!id || !title) {
    return null;
  }
  return {id, title};
};

const coerceCandidateDetailsFromMetadata = (
  raw: unknown,
): TaskCandidateDetails | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const id =
    record.id !== undefined && record.id !== null
      ? String(record.id).trim()
      : '';
  const name = typeof record.name === 'string' ? record.name.trim() : '';
  if (!id || !name) {
    return null;
  }
  return {id, name};
};

export const mapTaskDetailsToInitialValues = (
  taskDetails: TaskDetailsResponse,
  timezone?: string,
): InitialTaskReminderValues => {
  const tz = timezone ?? moment.tz.guess();
  const dueInstant = taskDetails.time_due
    ? moment.utc(taskDetails.time_due)
    : null;
  const isValidDueDateTime = Boolean(dueInstant?.isValid());
  const dueDateTime = isValidDueDateTime ? dueInstant!.tz(tz) : moment().tz(tz);

  return {
    title: taskDetails.title || '',
    description: getDescriptionFromTask(taskDetails.description),
    task_type: taskDetails.task_type || 'generic',
    assignee_id: taskDetails.assignee_id ? String(taskDetails.assignee_id) : '',
    cc: Array.isArray(taskDetails.cc)
      ? taskDetails.cc.map((ccValue) => String(ccValue))
      : [],
    due_date: isValidDueDateTime
      ? dueDateTime.format('YYYY-MM-DD')
      : moment().tz(tz).format('YYYY-MM-DD'),
    due_time: isValidDueDateTime
      ? dueDateTime.format('HH:mm')
      : moment().tz(tz).add(1, 'hour').startOf('hour').format('HH:mm'),
    status: taskDetails.status || 'created',
    reminders: mapApiRemindersToReminderOptions(taskDetails.reminders || []),
    job_details: coerceJobDetailsFromMetadata(
      taskDetails.metadata?.job_details,
    ),
    candidate_details: coerceCandidateDetailsFromMetadata(
      taskDetails.metadata?.candidate_details,
    ),
  };
};

export type BuildTaskMetadataOptions = {
  existingMetadata?: TaskMetadata | null | undefined;
  preserveJobCandidateFromExisting: boolean;
  displayConfig?: Array<TaskMetadataDisplayCtx>;
};

export const buildTaskMetadataForPayload = (
  formValues: Pick<TaskReminderFormState, 'job_details' | 'candidate_details'>,
  options: BuildTaskMetadataOptions,
): TaskMetadata | undefined => {
  const {existingMetadata, preserveJobCandidateFromExisting, displayConfig} =
    options;

  const hasFormJob = Boolean(
    formValues.job_details?.id && formValues.job_details?.title,
  );
  const hasFormCandidate = Boolean(
    formValues.candidate_details?.id && formValues.candidate_details?.name,
  );
  const hasDisplayConfig = Boolean(displayConfig?.length);

  if (!existingMetadata) {
    if (!hasFormJob && !hasFormCandidate && !hasDisplayConfig) {
      return undefined;
    }
    return {
      cta_config: [],
      ...(hasDisplayConfig ? {display_config: displayConfig} : {}),
      ...(hasFormJob ? {job_details: formValues.job_details!} : {}),
      ...(hasFormCandidate
        ? {candidate_details: formValues.candidate_details!}
        : {}),
    };
  }

  const merged: TaskMetadata = {
    ...existingMetadata,
    cta_config: existingMetadata.cta_config ?? [],
  };

  if (preserveJobCandidateFromExisting) {
    if (existingMetadata.job_details) {
      merged.job_details = existingMetadata.job_details;
    } else {
      delete merged.job_details;
    }
    if (existingMetadata.candidate_details) {
      merged.candidate_details = existingMetadata.candidate_details;
    } else {
      delete merged.candidate_details;
    }
    return merged;
  }

  if (hasDisplayConfig) {
    merged.display_config = displayConfig;
  }

  if (hasFormJob) {
    merged.job_details = formValues.job_details!;
  } else {
    delete merged.job_details;
  }
  if (hasFormCandidate) {
    merged.candidate_details = formValues.candidate_details!;
  } else {
    delete merged.candidate_details;
  }

  return merged;
};

/**
 * Derives the `entity_mappings` payload from the resolved task metadata.
 * Only includes a mapping when the corresponding job/candidate is tagged.
 */
export const buildEntityMappingsFromMetadata = (
  metadata: TaskMetadata | undefined,
): Array<TaskEntityMapping> | undefined => {
  if (!metadata) {
    return undefined;
  }
  const entityMappings: Array<TaskEntityMapping> = [];
  if (metadata.job_details?.id) {
    entityMappings.push({
      entity_id: String(metadata.job_details.id),
      entity_type: 'job',
    });
  }
  if (metadata.candidate_details?.id) {
    entityMappings.push({
      entity_id: String(metadata.candidate_details.id),
      entity_type: 'candidate',
    });
  }
  return entityMappings.length ? entityMappings : undefined;
};

export const buildCreateTaskPayload = (
  formValues: TaskReminderFormValues,
  metadataOptions?: BuildTaskMetadataOptions,
  timezone?: string,
): CreateTaskPayload => {
  const base: CreateTaskPayload = {
    title: formValues.title.trim(),
    description: parseDescription(formValues.description),
    assignee_id: formValues.assignee_id,
    task_type: formValues.task_type || 'generic',
    time_due: getDueDateTime(
      formValues.due_date,
      formValues.due_time,
      timezone,
    ),
    creator_type: 'user',
    cc: formValues.cc,
    reminders: mapReminderOptionsToApiReminders(formValues.reminders),
  };

  if (!metadataOptions) {
    return base;
  }

  const metadata = buildTaskMetadataForPayload(
    {
      job_details: formValues.job_details,
      candidate_details: formValues.candidate_details,
    },
    metadataOptions,
  );
  const entityMappings = buildEntityMappingsFromMetadata(metadata);

  return {
    ...base,
    ...(metadata ? {metadata} : {}),
    ...(entityMappings ? {entity_mappings: entityMappings} : {}),
  };
};

export const buildUpdateTaskPayload = (
  formValues: TaskReminderFormValues,
  metadataOptions?: BuildTaskMetadataOptions,
  timezone?: string,
): UpdateTaskPayload => {
  const base: UpdateTaskPayload = {
    title: formValues.title.trim(),
    description: parseDescription(formValues.description),
    assignee_id: formValues.assignee_id,
    time_due: getDueDateTime(
      formValues.due_date,
      formValues.due_time,
      timezone,
    ),
    cc: formValues.cc,
    reminders: mapReminderOptionsToApiReminders(formValues.reminders),
    status: formValues.status,
  };

  if (!metadataOptions) {
    return base;
  }

  const metadata = buildTaskMetadataForPayload(
    {
      job_details: formValues.job_details,
      candidate_details: formValues.candidate_details,
    },
    metadataOptions,
  );
  const entityMappings = buildEntityMappingsFromMetadata(metadata);

  return {
    ...base,
    ...(metadata ? {metadata} : {}),
    ...(entityMappings ? {entity_mappings: entityMappings} : {}),
  };
};

export const SEARCH_DEBOUNCE_MS = 400;
export const DEFAULT_PAGE_SIZE = 50;
export const TASK_LIST_PAGE_SIZE = 10;

export const DASHBOARD_TASKS_PREVIEW_LIMIT = 5;

export type DashboardTaskDueDisplay = {
  shortLabel: string;
  isDanger: boolean;
  tooltipBody: string;
};

export const getDashboardTaskDueDisplay = (
  timeDue: string | undefined,
  tab: 'overdue' | 'upcoming',
  t: TranslationFunction,
): DashboardTaskDueDisplay => {
  const defaultTimezone = moment.tz.guess();
  const raw = timeDue?.trim() ?? '';
  const dateTimeLine = getUTCDateWithTimezone(timeDue || '');
  const dueMoment = raw ? moment(raw).tz(defaultTimezone) : null;
  const now = moment().tz(defaultTimezone);

  if (!dueMoment || !dueMoment.isValid()) {
    return {
      shortLabel: dateTimeLine || '',
      isDanger: tab === 'overdue',
      tooltipBody: dateTimeLine,
    };
  }

  const isDueToday = dueMoment.isSame(now, 'day');

  if (tab === 'overdue') {
    return {
      shortLabel: isDueToday
        ? t('Due_today', 'Due today')
        : t('Overdue', 'Overdue'),
      isDanger: true,
      tooltipBody: dateTimeLine,
    };
  }

  return {
    shortLabel: t('Upcoming', 'Upcoming'),
    isDanger: false,
    tooltipBody: dateTimeLine,
  };
};

export const TAB_TO_DUE_STATUS: Record<TaskTab, TaskDueStatus> = {
  overdue: 'overdue',
  upcoming: 'upcoming',
  completed: 'complete',
};

export const TAB_CONFIGS: ReadonlyArray<TabConfig> = [
  {tabId: 'overdue', label: 'Overdue', iconName: 'circle-exclamation'},
  {tabId: 'upcoming', label: 'Upcoming', iconName: 'clock'},
  {tabId: 'completed', label: 'Completed', iconName: 'circle-check'},
];

export const EMPTY_STATE_KEYS: Record<
  TaskTab,
  {
    titleKey: string;
    titleFallback: string;
    descKey: string;
    descFallback: string;
  }
> = {
  overdue: {
    titleKey: 'No_Overdue_Tasks',
    titleFallback: 'No overdue tasks',
    descKey: 'You_dont_have_any_overdue_tasks',
    descFallback:
      'You don\'t have any overdue tasks. Great job staying on top of things!',
  },
  upcoming: {
    titleKey: 'No_Upcoming_Tasks',
    titleFallback: 'No upcoming tasks',
    descKey: 'You_dont_have_any_upcoming_tasks',
    descFallback:
      'You don\'t have any upcoming tasks. Create a new task to get started.',
  },
  completed: {
    titleKey: 'No_Completed_Tasks',
    titleFallback: 'No completed tasks',
    descKey: 'You_havent_completed_any_tasks_yet',
    descFallback: 'You haven\'t completed any tasks yet.',
  },
};

export const computeDateRange = (
  preset: DateFilterPreset,
): {dateFrom: string; dateTo: string} => {
  const now = moment();

  switch (preset) {
    case 'last_7':
      return {
        dateFrom: convertDateTimeToIsoString(
          now.clone().subtract(7, 'days').startOf('day'),
        ),
        dateTo: convertDateTimeToIsoString(now.clone().endOf('day')),
      };
    case 'last_30':
      return {
        dateFrom: convertDateTimeToIsoString(
          now.clone().subtract(30, 'days').startOf('day'),
        ),
        dateTo: convertDateTimeToIsoString(now.clone().endOf('day')),
      };
    case 'next_7':
      return {
        dateFrom: convertDateTimeToIsoString(now.clone().startOf('day')),
        dateTo: convertDateTimeToIsoString(
          now.clone().add(7, 'days').endOf('day'),
        ),
      };
    case 'next_30':
      return {
        dateFrom: convertDateTimeToIsoString(now.clone().startOf('day')),
        dateTo: convertDateTimeToIsoString(
          now.clone().add(30, 'days').endOf('day'),
        ),
      };
    default:
      return {dateFrom: '', dateTo: ''};
  }
};

export const defaultPresetForTab = (tab: TaskTab): DateFilterPreset =>
  tab === 'upcoming' ? 'next_7' : 'last_7';

export const buildDashboardTaskListParams = (
  tab: 'overdue' | 'upcoming',
  jobId?: number | string,
): TaskListParams => {
  const preset = defaultPresetForTab(tab);
  const {dateFrom, dateTo} = computeDateRange(preset);
  return {
    task_type: null,
    due_status: TAB_TO_DUE_STATUS[tab],
    title: null,
    assignment_type: 'me',
    time_from: dateFrom || null,
    time_to: dateTo || null,
    limit: DASHBOARD_TASKS_PREVIEW_LIMIT,
    offset: 0,
    entity_id: jobId,
    entity_type: jobId ? 'job' : null,
  };
};

/** Assignment segment in `/tasks` URL query (`assignee` param). */
export type TasksPageUrlAssignment = 'all' | TaskAssignmentType;

const isTasksPageDatePreset = (value: unknown): value is DateFilterPreset =>
  value === 'last_7' ||
  value === 'last_30' ||
  value === 'next_7' ||
  value === 'next_30' ||
  value === 'custom';

export const getTasksDateRangePickerDateBounds = (
  activeTab: TaskTab,
): {minDate?: string; maxDate?: string} =>
  activeTab === 'upcoming'
    ? {
        maxDate: convertDateTimeToIsoString(
          moment().add(10, 'years').endOf('day'),
        ),
      }
    : {
        maxDate: convertDateTimeToIsoString(moment().endOf('day')),
      };

const getTasksPageDateRangeFilterData = (
  timeDueFrom: string,
  timeDueTo: string,
  tabId: TaskTab,
): {timeDueFrom: string; timeDueTo: string} => {
  const {maxDate} = getTasksDateRangePickerDateBounds(tabId);
  if (!maxDate) {
    return {timeDueFrom, timeDueTo};
  }
  const start = moment(timeDueFrom);
  const end = moment(timeDueTo);
  const max = moment(maxDate);
  if (!start.isValid() || !end.isValid() || !max.isValid()) {
    return {timeDueFrom, timeDueTo};
  }
  let nextEnd = end;
  if (nextEnd.isAfter(max)) {
    nextEnd = max.clone();
  }
  let nextStart = start;
  if (nextStart.isAfter(nextEnd)) {
    nextStart = nextEnd.clone();
  }
  return {
    timeDueFrom: convertDateTimeToIsoString(nextStart),
    timeDueTo: convertDateTimeToIsoString(nextEnd),
  };
};

export const inferTaskDateFilterMenuKey = (
  timeDueFrom: string,
  timeDueTo: string,
  activeTab: TaskTab,
): DateFilterPreset => {
  const urlFrom = moment(timeDueFrom);
  const urlTo = moment(timeDueTo);
  if (!timeDueFrom || !timeDueTo || !urlFrom.isValid() || !urlTo.isValid()) {
    return 'custom';
  }
  const urlFromMs = urlFrom.valueOf();
  const urlToMs = urlTo.valueOf();

  const presetKeys: Array<DateFilterPreset> =
    activeTab === 'upcoming' ? ['next_7', 'next_30'] : ['last_7', 'last_30'];

  for (const key of presetKeys) {
    const {dateFrom, dateTo} = computeDateRange(key);
    const presetFrom = moment(dateFrom);
    const presetTo = moment(dateTo);
    if (!presetFrom.isValid() || !presetTo.isValid()) {
      continue;
    }
    if (presetFrom.valueOf() === urlFromMs && presetTo.valueOf() === urlToMs) {
      return key;
    }
  }

  return 'custom';
};

const isTasksPageUrlAssignment = (
  value: unknown,
): value is TasksPageUrlAssignment =>
  value === 'all' || value === 'me' || value === 'cc' || value === 'team';

export const getTasksPageQueryString = (
  query: Record<string, unknown>,
  key: string,
): string => (typeof query[key] === 'string' ? String(query[key]) : '');

export const coerceTasksPageQueryToStringMap = (
  maybeQuery: Record<string, unknown>,
): Record<string, string> =>
  Object.entries(maybeQuery).reduce<Record<string, string>>((acc, [k, v]) => {
    if (typeof v === 'string') {
      acc[k] = v;
    }
    return acc;
  }, {});

export const buildTasksPageNextQuery = (
  prevQuery: Record<string, unknown>,
  updates: Record<string, string | number | null | undefined>,
): Record<string, string> => {
  const next: Record<string, string> =
    coerceTasksPageQueryToStringMap(prevQuery);
  for (const [key, value] of Object.entries(updates)) {
    if (value === '' || value === null || value === undefined) {
      delete next[key];
      continue;
    }
    next[key] = typeof value === 'number' ? String(value) : String(value);
  }
  return next;
};

export const resolveTasksTabFromQuery = (
  query: Record<string, unknown>,
): TabConfig =>
  TAB_CONFIGS.find((c) => c.tabId === getTasksPageQueryString(query, 'tab')) ??
  TAB_CONFIGS[0];

export type ParsedTasksFilterStateFromUrl = {
  searchQuery: string;
  taskType: string;
  assignmentType: TasksPageUrlAssignment;
  timeDueFrom: string;
  timeDueTo: string;
};

export const parseTasksFilterStateFromQuery = (
  query: Record<string, unknown>,
  tab: TabConfig,
): ParsedTasksFilterStateFromUrl => {
  const searchQuery = getTasksPageQueryString(query, 'q');
  const taskType = getTasksPageQueryString(query, 'taskType');
  const rawAssignee = getTasksPageQueryString(query, 'assignee');
  const assignmentType: TasksPageUrlAssignment = isTasksPageUrlAssignment(
    rawAssignee,
  )
    ? rawAssignee
    : 'me';

  const queryFrom = getTasksPageQueryString(query, 'from');
  const queryTo = getTasksPageQueryString(query, 'to');

  let timeDueFrom: string;
  let timeDueTo: string;

  if (queryFrom && queryTo) {
    const clamped = getTasksPageDateRangeFilterData(
      queryFrom,
      queryTo,
      tab.tabId,
    );
    timeDueFrom = clamped.timeDueFrom;
    timeDueTo = clamped.timeDueTo;
  } else {
    const rawPreset = getTasksPageQueryString(query, 'preset');
    const legacyPreset: DateFilterPreset = isTasksPageDatePreset(rawPreset)
      ? rawPreset
      : defaultPresetForTab(tab.tabId);
    const effectivePreset =
      legacyPreset === 'custom' ? defaultPresetForTab(tab.tabId) : legacyPreset;
    const computed = computeDateRange(effectivePreset);
    timeDueFrom = computed.dateFrom;
    timeDueTo = computed.dateTo;
  }

  return {
    searchQuery,
    taskType,
    assignmentType,
    timeDueFrom,
    timeDueTo,
  };
};

/** Builds POST /tasks body from `/tasks` URL query (tab, filters, pagination defaults). */
export const taskListParamsFromQuery = (
  query: Record<string, unknown>,
): TaskListParams => {
  const currentTab = resolveTasksTabFromQuery(query);
  const {searchQuery, taskType, assignmentType, timeDueFrom, timeDueTo} =
    parseTasksFilterStateFromQuery(query, currentTab);
  return {
    task_type: taskType === 'all' || !taskType ? null : taskType,
    due_status: TAB_TO_DUE_STATUS[currentTab.tabId],
    title: searchQuery || null,
    assignment_type: assignmentType || 'all',
    time_from: timeDueFrom || null,
    time_to: timeDueTo || null,
    limit: TASK_LIST_PAGE_SIZE,
    offset: 0,
  };
};

export const isDescriptionEmpty = (
  description: Record<string, unknown> | undefined | null,
): boolean => {
  if (description == null || typeof description !== 'object') {
    return true;
  }
  const blocks = description.blocks as Array<{text?: string}> | undefined;
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return true;
  }
  return !blocks.some((block) => (block.text ?? '').trim().length > 0);
};

export const dueDateAndTimeToSnoozeIso = (
  dueDate: string,
  dueTime: string,
  timezone?: string,
): string | null => {
  const tz = timezone ?? moment.tz.guess();
  const datePart = dueDate.trim() || moment().tz(tz).format('YYYY-MM-DD');
  const timePart =
    dueTime.trim() ||
    moment().tz(tz).startOf('day').add(9, 'hours').format('HH:mm');
  const parsed = moment.tz(`${datePart} ${timePart}`, 'YYYY-MM-DD HH:mm', tz);
  if (!parsed.isValid()) {
    return null;
  }
  return convertDateTimeToIsoString(parsed);
};

export const buildSnoozeOptions = (
  t: (key: string, fallback: string) => string,
): Array<SnoozeOption> => {
  const now = moment();
  const laterToday = now.clone().add(3, 'hours');
  const tomorrow = now.clone().add(1, 'day').startOf('day').add(9, 'hours');
  const nextWeek = now
    .clone()
    .add(1, 'week')
    .startOf('isoWeek')
    .add(9, 'hours');

  return [
    {
      key: 'later_today',
      label: t('Later_Today', 'Later Today'),
      sublabel: laterToday.format('MMM D [at] h:mm A'),
      value: convertDateTimeToIsoString(laterToday),
    },
    {
      key: 'tomorrow',
      label: t('Tomorrow', 'Tomorrow'),
      sublabel: tomorrow.format('MMM D [at] h:mm A'),
      value: convertDateTimeToIsoString(tomorrow),
    },
    {
      key: 'next_week',
      label: t('Next_Week', 'Next Week'),
      sublabel: nextWeek.format('MMM D [at] h:mm A'),
      value: convertDateTimeToIsoString(nextWeek),
    },
    {
      key: 'custom_date',
      label: t('Pick_a_date_and_time', 'Pick a date and time'),
      sublabel: '',
      value: '',
    },
  ];
};

const trackTaskEvent = (
  eventName: typeof TASK_AND_REMINDER[keyof typeof TASK_AND_REMINDER],
  props: Record<string, unknown>,
) => {
  AnalyticsService.track(eventName, props);
};

export const trackTaskEditEvents = (
  taskEditId: number | string | null,
  taskDetails: TaskDetailsResponse | null,
  submitValues: TaskReminderFormValues,
) => {
  const changedFields: Array<string> = [];
  if (taskDetails?.title !== submitValues.title) {
    changedFields.push('title');
  }
  if (String(taskDetails?.assignee_id) !== String(submitValues.assignee_id)) {
    changedFields.push('assignee_id');
  }
  if (taskDetails?.time_due !== submitValues.due_date) {
    changedFields.push('due_date');
  }
  if (taskDetails?.status !== submitValues.status) {
    changedFields.push('status');
  }

  const isDueDateChanged = taskDetails?.time_due !== submitValues.due_date;

  trackTaskEvent(TASK_AND_REMINDER.TASK_EDITED, {
    task_id: taskEditId,
    fields_changed: changedFields,
    due_date_changed: isDueDateChanged ? 'Y' : 'N',
    prev_due_date: taskDetails?.time_due,
    new_due_date: submitValues.due_date,
  });

  if (String(taskDetails?.assignee_id) !== String(submitValues.assignee_id)) {
    trackTaskEvent(TASK_AND_REMINDER.TASK_REASSIGNED, {
      task_id: taskEditId,
      prev_assignee_id: taskDetails?.assignee_id,
      new_assignee_id: submitValues.assignee_id,
    });
  }

  if (
    submitValues.status === 'completed' &&
    taskDetails?.status !== 'completed'
  ) {
    trackTaskEvent(TASK_AND_REMINDER.TASK_COMPLETED, {
      task_id: taskEditId,
      completion_source: 'task_card',
      task_type: taskDetails?.task_type,
      overdue: taskDetails?.time_due
        ? new Date(taskDetails.time_due) < new Date()
        : false,
    });
  }
};

export const getFilledFieldCount = (values: TaskReminderFormState): number => {
  let count = 0;
  if (values.title.trim()) {
    count++;
  }
  if (values.description.trim()) {
    count++;
  }
  if (values.task_type.trim()) {
    count++;
  }
  if (values.assignee_id.trim()) {
    count++;
  }
  if (values.due_date.trim()) {
    count++;
  }
  if (values.due_time.trim()) {
    count++;
  }
  if (values.cc.length > 0) {
    count++;
  }
  if (values.reminders.some((r) => r.option)) {
    count++;
  }
  if (values.job_details?.id) {
    count++;
  }
  if (values.candidate_details?.id) {
    count++;
  }
  return count;
};

const TASK_TYPE_TO_ROUTE = [
  {
    taskType: 'generic',
    iconName: 'list-check',
  },
  {
    taskType: 'schedule_interview',
    iconName: 'calendar',
  },
  {
    taskType: 'collect_interview_feedback',
    iconName: 'clipboard-check',
  },
  {
    taskType: 'send_review_request',
    iconName: 'paper-plane',
  },
  {
    taskType: 'collect_review_feedback',
    iconName: 'comment-dots',
  },
  {
    taskType: 'send_candidate_form',
    iconName: 'file-lines',
  },
  {
    taskType: 'send_internal_form',
    iconName: 'file-pen',
  },
  {
    taskType: 'send_assessment',
    iconName: 'clipboard-list',
  },
  {
    taskType: 'generate_offer',
    iconName: 'file-contract',
  },
  {
    taskType: 'request_for_offer_approval',
    iconName: 'file-signature',
  },
  {
    taskType: 'requested_for_approval',
    iconName: 'file-signature',
  },
  {
    taskType: 'send_offer_to_candidate',
    iconName: 'file-contract',
  },
  {
    taskType: 'move_next_stage',
    iconName: 'arrow-up-right-dots',
  },
];

export const getTaskIconName = (taskType: string): string => {
  return (
    TASK_TYPE_TO_ROUTE.find((task) => task.taskType === taskType)?.iconName ||
    'list-check'
  );
};
